import { useEffect, useState } from "react";
import api, { formatApiError } from "@/lib/api";
import { Mail, Send } from "lucide-react";
import { toast } from "sonner";

export const EmailTestCard = ({ defaultTo = "" }) => {
  const [status, setStatus] = useState(null);
  const [to, setTo] = useState(defaultTo);
  const [busy, setBusy] = useState(false);
  const [result, setResult] = useState(null);

  useEffect(() => {
    api.get("/admin/email/status").then(({ data }) => setStatus(data)).catch(() => setStatus(null));
  }, []);

  const send = async () => {
    setBusy(true);
    setResult(null);
    try {
      const { data } = await api.post("/admin/email/test", { to });
      setResult(data);
      if (!data.sent) toast.error(data.detail);
      else if (data.mode === "console") toast.message("SMTP not configured — email written to server log only");
      else toast.success(`Test email sent to ${data.to}`);
    } catch (e) {
      toast.error(formatApiError(e));
    } finally {
      setBusy(false);
    }
  };

  return (
    <div className="border border-[var(--dojo-border)] bg-[var(--dojo-paper)] p-6 mb-8" data-testid="email-test-card">
      <div className="flex items-center gap-2 text-[10px] uppercase tracking-[0.24em] text-[var(--dojo-ink-soft)] mb-3">
        <Mail size={14} /> Email (SMTP)
      </div>
      <div className="text-sm mb-4" data-testid="email-status-text">
        {status?.configured
          ? <>Sending from <b>{status.sender}</b> via {status.host}:{status.port}</>
          : "SMTP is not configured yet — emails are only written to the server log."}
      </div>
      <div className="flex flex-col sm:flex-row gap-3">
        <input
          type="email"
          value={to}
          onChange={(e) => setTo(e.target.value)}
          placeholder="Send test to…"
          className="flex-1 border border-[var(--dojo-border)] bg-transparent px-3 py-2 text-sm"
          data-testid="email-test-input"
        />
        <button
          onClick={send}
          disabled={busy || !to}
          className="inline-flex items-center justify-center gap-2 px-5 py-2 text-[11px] uppercase tracking-[0.2em] bg-[var(--dojo-ink)] text-[var(--dojo-paper)] transition-opacity hover:opacity-90 disabled:opacity-50"
          data-testid="email-test-send-button"
        >
          <Send size={14} /> {busy ? "Sending…" : "Send test email"}
        </button>
      </div>
      {result && (
        <div className={`mt-3 text-xs ${result.sent ? "text-[var(--dojo-green)]" : "text-red-600"}`} data-testid="email-test-result">
          {result.detail}
        </div>
      )}
    </div>
  );
};
